import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { signInWithCustomToken, signOut } from "firebase/auth";
import Icon from "../components/Icon";
import { auth } from "../firebase";
import designTokens from "../styles/designTokens";
import "../styles/fonts.css";
import { appToast } from "../utils/appToast";
import {
  attachPairingByCode,
  consumePairingSession,
  DevicePairingAttachError,
  DEVICE_PAIRING_TTL_MS,
  fetchPairingStatus,
} from "../services/devicePairingClient";
import {
  handleWebViewLoginSuccess,
  isViewRNativeAuthWebView,
} from "../utils/webViewUtils";

export const STUDIO_CODE_LAYOUT =
  designTokens.semantic.avpAuthentication.codeSignIn;

function pairingAttachErrorMessage(status: number): string {
  if (status === 404) {
    return "Code doesn't match this app. Use a code from the same environment.";
  }
  if (status === 409) {
    return "Code already used. Generate a new code in View R Studio.";
  }
  if (status === 410) {
    return "Code expired. Get a new one in View R Studio.";
  }
  if (status === 429) {
    return "Too many attempts. Wait and try again.";
  }
  return "Invalid code. Try again.";
}

/**
 * Figma Code Sign in 13345:5040 — default 9096:29095 / invalid code 9096:29097.
 */
const StudioCodeLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const isNativeAvpWebView = useMemo(() => isViewRNativeAuthWebView(), []);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const hasInlineError = inlineError !== null;
  const busyRef = useRef(false);
  const connectCancelledRef = useRef(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const codeRef = useRef("");
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  const focusCell = (i: number) => {
    requestAnimationFrame(() => inputRefs.current[i]?.focus());
  };

  /** 잘못된 코드·만료·타임아웃 등 실패 시 6칸 전부 비우고 첫 칸으로 포커스 */
  const resetCodeInputs = useCallback(() => {
    setCode("");
    codeRef.current = "";
    window.setTimeout(() => focusCell(0), 0);
  }, []);

  const showFeedback = useCallback(
    (message: string, tone: "error" | "complete" = "error") => {
      if (isNativeAvpWebView) {
        if (tone === "error") {
          setInlineError(message);
        }
        return;
      }
      appToast(
        message,
        tone === "error" ? "error" : "complete",
        tone === "error" ? 4000 : 2500,
      );
    },
    [isNativeAvpWebView],
  );

  const clearConnectState = useCallback(() => {
    connectCancelledRef.current = true;
    busyRef.current = false;
    setBusy(false);
  }, []);

  useEffect(() => {
    focusCell(0);
  }, []);

  const handleChange = (index: number, raw: string) => {
    if (inlineError) setInlineError(null);
    const filtered = raw.replace(/[^A-Z0-9]/gi, "").toUpperCase();
    const prev = codeRef.current;
    if (!filtered) {
      const next = prev.slice(0, index) + prev.slice(index + 1);
      codeRef.current = next;
      setCode(next);
      return;
    }
    if (filtered.length > 1) {
      const merged = (prev.slice(0, index) + filtered).slice(0, 6);
      codeRef.current = merged;
      setCode(merged);
      window.setTimeout(() => focusCell(Math.min(merged.length, 5)), 0);
      return;
    }
    const ch = filtered[0];
    let next = prev.slice(0, index) + ch + prev.slice(index + 1);
    if (next.length > 6) next = next.slice(0, 6);
    codeRef.current = next;
    setCode(next);
    window.setTimeout(() => {
      if (index < 5) focusCell(index + 1);
    }, 0);
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key !== "Backspace") return;
    if ((e.target as HTMLInputElement).value) return;
    e.preventDefault();
    if (index > 0) {
      const prev = codeRef.current;
      const next = prev.slice(0, index - 1) + prev.slice(index);
      codeRef.current = next;
      setCode(next);
      focusCell(index - 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const t = e.clipboardData
      .getData("text")
      .replace(/[^A-Z0-9]/gi, "")
      .toUpperCase()
      .slice(0, 6);
    codeRef.current = t;
    setCode(t);
    focusCell(Math.min(t.length, 5));
  };

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    });

  const handleBackToLogin = useCallback(() => {
    clearConnectState();
    navigate("/login");
  }, [clearConnectState, navigate]);

  const handleConnect = useCallback(async () => {
    if (busyRef.current) return;
    const raw = codeRef.current.trim().toUpperCase();
    if (raw.length !== 6) return;
    connectCancelledRef.current = false;
    setInlineError(null);
    busyRef.current = true;
    setBusy(true);
    try {
      const { sessionId, consumerKey } = await attachPairingByCode(raw);
      const pollDeadline = Date.now() + DEVICE_PAIRING_TTL_MS + 60_000;
      while (Date.now() < pollDeadline) {
        if (connectCancelledRef.current) return;
        const st = await fetchPairingStatus(sessionId, consumerKey);
        if (connectCancelledRef.current) return;
        if (st.status === "completed" && st.custom_token) {
          const credential = await signInWithCustomToken(auth, st.custom_token);
          const idToken = await credential.user.getIdToken();
          try {
            await consumePairingSession(sessionId, idToken);
          } catch {
            // 로그인은 이미 성공했다. consume 실패 세션은 TTL 정리 대상으로 남긴다.
          }
          if (isNativeAvpWebView) {
            handleWebViewLoginSuccess(idToken, {
              uid: credential.user.uid,
              email: credential.user.email,
              displayName: credential.user.displayName,
              provider: credential.user.providerData[0]?.providerId || "custom",
              isGuest: false,
            });
            return;
          }
          showFeedback("Logged in successfully.", "complete");
          return;
        }
        if (st.status === "expired") {
          showFeedback(
            "Code expired. Get a new one in View R Studio.",
            "error",
          );
          resetCodeInputs();
          return;
        }
        await sleep(1500);
      }
      showFeedback("Pairing timed out. Check the code and try again.", "error");
      resetCodeInputs();
    } catch (err) {
      if (isNativeAvpWebView && auth.currentUser) {
        try {
          await signOut(auth);
        } catch {
          /* partial session cleanup best-effort */
        }
      }
      if (err instanceof DevicePairingAttachError) {
        showFeedback(pairingAttachErrorMessage(err.status), "error");
      } else {
        showFeedback("Invalid code. Try again.", "error");
      }
      resetCodeInputs();
    } finally {
      if (!connectCancelledRef.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }, [isNativeAvpWebView, resetCodeInputs, showFeedback]);

  return (
    <Box
      sx={{
        width: "100%",
        height: STUDIO_CODE_LAYOUT.canvas.height,
        minHeight: STUDIO_CODE_LAYOUT.canvas.height,
        maxHeight: STUDIO_CODE_LAYOUT.canvas.height,
        bgcolor: STUDIO_CODE_LAYOUT.canvas.background,
        color: designTokens.fill.text.common100.dark,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: `${STUDIO_CODE_LAYOUT.canvas.sectionGap}px`,
        px: "24px",
        pb: `${STUDIO_CODE_LAYOUT.canvas.bottomPadding}px`,
        borderRadius: STUDIO_CODE_LAYOUT.canvas.radius,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
      data-testid="studio-code-canvas"
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: STUDIO_CODE_LAYOUT.navigation.width,
          height: STUDIO_CODE_LAYOUT.navigation.height,
          flexShrink: 0,
          position: "relative",
          boxSizing: "border-box",
        }}
        data-testid="studio-code-navigation"
      >
        <IconButton
          aria-label="Back"
          onClick={handleBackToLogin}
          sx={{
            width: 44,
            height: 44,
            p: 0,
            position: "absolute",
            left: 0,
            top: STUDIO_CODE_LAYOUT.navigation.backButtonTop,
            borderRadius: "999px",
            bgcolor: designTokens.fill.shape.opacityWhite10.dark,
            color: designTokens.fill.text.common100.dark,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
          }}
        >
          <Icon
            name="left-arrow-16"
            size={19}
            style={{ transform: "translateX(4px)" }}
          />
        </IconButton>
        <Typography
          sx={{
            position: "absolute",
            top: STUDIO_CODE_LAYOUT.navigation.titleTop,
            left: "50%",
            transform: "translateX(-50%)",
            width: STUDIO_CODE_LAYOUT.canvas.width,
            height: STUDIO_CODE_LAYOUT.navigation.titleHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            fontFamily: designTokens.typography.fontFamily.roboto,
            fontSize: "28px",
            fontWeight: 700,
            lineHeight: "33px",
            color: designTokens.fill.text.common100.dark,
          }}
        >
          Sign in with a code
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: `${STUDIO_CODE_LAYOUT.content.sectionGap}px`,
          width: STUDIO_CODE_LAYOUT.content.width,
          height: STUDIO_CODE_LAYOUT.content.height,
          flex: "none",
        }}
        data-testid="studio-code-main"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: `${STUDIO_CODE_LAYOUT.content.instructionsToInputGap}px`,
            width: STUDIO_CODE_LAYOUT.content.width,
            height:
              STUDIO_CODE_LAYOUT.content.instructionsHeight +
              STUDIO_CODE_LAYOUT.content.instructionsToInputGap +
              STUDIO_CODE_LAYOUT.input.height +
              (hasInlineError
                ? STUDIO_CODE_LAYOUT.feedback.errorGap +
                  STUDIO_CODE_LAYOUT.feedback.errorHeight
                : STUDIO_CODE_LAYOUT.feedback.defaultSlotHeight),
            flexShrink: 0,
          }}
          data-testid="studio-code-verification-container"
        >
          <Typography
            sx={{
              width: STUDIO_CODE_LAYOUT.content.instructionsWidth,
              height: STUDIO_CODE_LAYOUT.content.instructionsHeight,
              fontFamily: designTokens.typography.fontFamily.roboto,
              fontSize: "17px",
              fontWeight: 400,
              lineHeight: "20px",
              color: designTokens.fill.text.grey90.dark,
              textAlign: "center",
            }}
          >
            Go to the Account section in your View R Studio
            <br />
            and click “Connect to Vision Pro” to get the code
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: hasInlineError
                ? `${STUDIO_CODE_LAYOUT.feedback.errorGap}px`
                : 0,
              width: STUDIO_CODE_LAYOUT.content.width,
              height:
                STUDIO_CODE_LAYOUT.input.height +
                (hasInlineError
                  ? STUDIO_CODE_LAYOUT.feedback.errorGap +
                    STUDIO_CODE_LAYOUT.feedback.errorHeight
                  : STUDIO_CODE_LAYOUT.feedback.defaultSlotHeight),
              flexShrink: 0,
            }}
            data-testid="studio-code-input-section"
          >
            <Box
              onPaste={handlePaste}
              sx={{
                display: "flex",
                gap: `${STUDIO_CODE_LAYOUT.input.gap}px`,
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "nowrap",
                width: STUDIO_CODE_LAYOUT.content.width,
                height: STUDIO_CODE_LAYOUT.input.height,
                flexShrink: 0,
              }}
              data-testid="studio-code-input-row"
            >
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Box
                  key={i}
                  onClick={() => focusCell(i)}
                  sx={{
                    width: STUDIO_CODE_LAYOUT.input.width,
                    height: STUDIO_CODE_LAYOUT.input.height,
                    borderRadius: STUDIO_CODE_LAYOUT.input.radius,
                    bgcolor: STUDIO_CODE_LAYOUT.input.background,
                    position: "relative",
                    overflow: "hidden",
                    boxSizing: "border-box",
                    boxShadow: STUDIO_CODE_LAYOUT.input.shadow,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <input
                    ref={(el) => {
                      inputRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="text"
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    autoFocus={i === 0}
                    aria-label={`Code character ${i + 1} of 6`}
                    maxLength={1}
                    disabled={busy}
                    value={code[i] ?? ""}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => {
                      handleKeyDown(i, e);
                      if (
                        e.key === "Enter" &&
                        codeRef.current.length === 6 &&
                        !busyRef.current
                      ) {
                        e.preventDefault();
                        void handleConnect();
                      }
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      background: "transparent",
                      outline: "none",
                      textAlign: "center",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 24,
                      fontWeight: 700,
                      lineHeight: "normal",
                      color: designTokens.fill.text.common100.dark,
                      textTransform: "uppercase",
                      padding: 0,
                    }}
                  />
                </Box>
              ))}
            </Box>

            <Box
              data-testid="studio-code-feedback-slot"
              aria-label="Pairing feedback"
              aria-live="polite"
              sx={{
                width: STUDIO_CODE_LAYOUT.content.width,
                height: hasInlineError
                  ? STUDIO_CODE_LAYOUT.feedback.errorHeight
                  : STUDIO_CODE_LAYOUT.feedback.defaultSlotHeight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "visible",
              }}
            >
              {inlineError ? (
                <Typography
                  role="alert"
                  sx={{
                    width: "100%",
                    minHeight: STUDIO_CODE_LAYOUT.feedback.errorHeight,
                    maxHeight: STUDIO_CODE_LAYOUT.feedback.errorHeight * 2,
                    textAlign: "center",
                    fontFamily: designTokens.typography.fontFamily.roboto,
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: `${STUDIO_CODE_LAYOUT.feedback.errorHeight}px`,
                    color: STUDIO_CODE_LAYOUT.feedback.color,
                    whiteSpace: "normal",
                  }}
                >
                  {inlineError}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>

        <Button
          variant="contained"
          disabled={code.length !== 6 || busy}
          onClick={() => void handleConnect()}
          fullWidth
          aria-busy={busy}
          data-testid="studio-code-connect-button"
          sx={{
            width: STUDIO_CODE_LAYOUT.button.width,
            minWidth: STUDIO_CODE_LAYOUT.button.width,
            height: STUDIO_CODE_LAYOUT.button.height,
            p: 0,
            borderRadius: STUDIO_CODE_LAYOUT.button.radius,
            textTransform: "none",
            bgcolor: STUDIO_CODE_LAYOUT.button.background,
            color: "rgba(255,255,255,0.8)",
            fontFamily: designTokens.typography.fontFamily.roboto,
            fontSize: "20px",
            fontWeight: 600,
            lineHeight: 1,
            boxShadow: "none",
            "&:hover": {
              bgcolor: STUDIO_CODE_LAYOUT.button.background,
              backgroundImage: `linear-gradient(${STUDIO_CODE_LAYOUT.button.selectedOverlay}, ${STUDIO_CODE_LAYOUT.button.selectedOverlay})`,
              color: designTokens.fill.text.common100.dark,
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              ...(busy
                ? {
                    bgcolor: STUDIO_CODE_LAYOUT.button.background,
                    color: designTokens.fill.text.common100.dark,
                    opacity: 0.95,
                  }
                : hasInlineError
                  ? {
                      bgcolor: STUDIO_CODE_LAYOUT.button.background,
                      backgroundImage: `linear-gradient(${STUDIO_CODE_LAYOUT.button.selectedOverlay}, ${STUDIO_CODE_LAYOUT.button.selectedOverlay})`,
                      color: designTokens.fill.text.common100.dark,
                    }
                  : {
                      bgcolor: designTokens.fill.shape.grey40.dark,
                      color: designTokens.fill.text.grey60.dark,
                    }),
            },
          }}
        >
          {busy ? (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              <CircularProgress
                size={22}
                thickness={4}
                sx={{ color: "inherit" }}
              />
              Connecting…
            </Box>
          ) : (
            "Connect"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default StudioCodeLoginPage;
